import { ReactNode, CSSProperties } from 'react';
import { useInView } from '@/hooks/useInView';

export type RevealVariant = 'fadeUp' | 'fadeDown' | 'fadeLeft' | 'fadeRight' | 'fade' | 'scaleUp' | 'slideUp';

interface RevealProps {
  children: ReactNode;
  variant?: RevealVariant;
  delay?: number;
  duration?: number;
  className?: string;
  threshold?: number;
  as?: keyof JSX.IntrinsicElements;
}

const hiddenStyles: Record<RevealVariant, CSSProperties> = {
  fadeUp:    { opacity: 0, transform: 'translateY(32px)' },
  fadeDown:  { opacity: 0, transform: 'translateY(-24px)' },
  fadeLeft:  { opacity: 0, transform: 'translateX(40px)' },
  fadeRight: { opacity: 0, transform: 'translateX(-40px)' },
  fade:      { opacity: 0 },
  scaleUp:   { opacity: 0, transform: 'scale(0.92)' },
  slideUp:   { opacity: 0, transform: 'translateY(48px)' },
};

const visibleStyles: Record<RevealVariant, CSSProperties> = {
  fadeUp:    { opacity: 1, transform: 'translateY(0)' },
  fadeDown:  { opacity: 1, transform: 'translateY(0)' },
  fadeLeft:  { opacity: 1, transform: 'translateX(0)' },
  fadeRight: { opacity: 1, transform: 'translateX(0)' },
  fade:      { opacity: 1 },
  scaleUp:   { opacity: 1, transform: 'scale(1)' },
  slideUp:   { opacity: 1, transform: 'translateY(0)' },
};

export default function Reveal({
  children,
  variant = 'fadeUp',
  delay = 0,
  duration = 700,
  className = '',
  threshold = 0.15,
  as: Tag = 'div',
}: RevealProps) {
  const [ref, inView] = useInView<HTMLDivElement>({ threshold, rootMargin: '0px 0px -50px 0px' });

  const style: CSSProperties = {
    ...(inView ? visibleStyles[variant] : hiddenStyles[variant]),
    transition: `opacity ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), transform ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
    transitionDelay: `${delay}ms`,
    willChange: 'opacity, transform',
  };

  return (
    <Tag ref={ref as never} className={className} style={style}>
      {children}
    </Tag>
  );
}
